/**
 * ShapeZoomViewer - a read-only viewer that adds zoom and pan on top of the
 * scale-to-fit rendering ShapeThumbnail provides.
 *
 * Built for drawings whose fine detail is illegible at fit scale (the
 * architect's ask, 2026-09-06): fit shows the whole composition, and from
 * there the person zooms toward the cursor and drags to the part they care
 * about. It deliberately does NOT re-implement rendering - it hosts a
 * `<ptc-thumbnail fit="contain">` inside a transformed stage, so the drawing
 * a person zooms into is pixel-for-pixel the one every other surface shows.
 *
 * Scale model: 1 = fit (the thumbnail's own scale-to-fit result), clamped to
 * [1, 8]. Zooming below fit is disallowed on purpose - fit already shows
 * everything, and a smaller drawing answers no question. Pan is enabled only
 * above fit scale for the same reason, and Fit resets both.
 *
 * @element ptc-zoom-viewer
 */

import shapeZoomViewerStyles from './ShapeZoomViewer.styles.css?inline';
import { LitElement, html, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { GridComposition } from '../models/GridModel';
import type { AssetManager } from '../models/AssetManager';
import './ShapeThumbnail';

import '@shoelace-style/shoelace/dist/components/icon-button/icon-button.js';
import '@shoelace-style/shoelace/dist/components/tooltip/tooltip.js';

/** Matches the editor toolbar's step feel; multiplicative so steps look even at any scale. */
const ZOOM_STEP = 1.25;
const MIN_SCALE = 1;
const MAX_SCALE = 8;

@customElement('ptc-zoom-viewer')
export class ShapeZoomViewer extends LitElement {
  static styles = unsafeCSS(shapeZoomViewerStyles);

  /** Authored grid composition to render (same input the thumbnail takes). */
  @property({ type: Object })
  grid: GridComposition | null = null;

  /** Asset manager for the drawing's opening assets. */
  @property({ attribute: false })
  assetManager: AssetManager | null = null;

  /** Whether the hosted thumbnail draws its dimension annotations. */
  @property({ type: Boolean, attribute: 'show-dimensions' })
  showDimensions = false;

  @state() private scale = 1;
  @state() private tx = 0;
  @state() private ty = 0;
  @state() private panning = false;

  /** Pointer-drag bookkeeping; a class field, not state - it never renders. */
  private dragLast: { x: number; y: number } | null = null;

  protected updated(changed: Map<string, unknown>): void {
    // A different drawing invalidates the current viewpoint - what was under
    // the old zoom is not in the new composition. Reset rather than carry a
    // stale transform onto unrelated content.
    if (changed.has('grid')) {
      this.resetView();
    }
  }

  private resetView(): void {
    this.scale = 1;
    this.tx = 0;
    this.ty = 0;
  }

  /**
   * Zoom about a stage-space point, keeping that point stationary on screen.
   * With transform-origin 0 0: content = (point - translation) / scale, so the
   * new translation is point - content * newScale.
   */
  private zoomAt(pointX: number, pointY: number, factor: number): void {
    const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, this.scale * factor));
    if (next === this.scale) return;
    const contentX = (pointX - this.tx) / this.scale;
    const contentY = (pointY - this.ty) / this.scale;
    this.tx = pointX - contentX * next;
    this.ty = pointY - contentY * next;
    this.scale = next;
    if (this.scale === MIN_SCALE) {
      // Landing back on fit recentres exactly - a fit view with residual pan
      // reads as a rendering bug, not a viewpoint.
      this.tx = 0;
      this.ty = 0;
    }
  }

  /** Button zoom acts about the stage centre - there is no cursor to honour. */
  private zoomAtCentre(factor: number): void {
    const stage = this.renderRoot.querySelector<HTMLElement>('.stage');
    if (!stage) return;
    this.zoomAt(stage.clientWidth / 2, stage.clientHeight / 2, factor);
  }

  private onWheel = (e: WheelEvent): void => {
    // The stage owns the wheel entirely: without preventDefault the page
    // scrolls under the drawing mid-zoom, and ctrl+wheel triggers browser
    // page-zoom - both destroy the "point under the cursor" contract.
    e.preventDefault();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    this.zoomAt(
      e.clientX - rect.left,
      e.clientY - rect.top,
      e.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP
    );
  };

  private onPointerDown = (e: PointerEvent): void => {
    if (this.scale <= MIN_SCALE) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    this.dragLast = { x: e.clientX, y: e.clientY };
    this.panning = true;
  };

  private onPointerMove = (e: PointerEvent): void => {
    if (!this.dragLast) return;
    this.tx += e.clientX - this.dragLast.x;
    this.ty += e.clientY - this.dragLast.y;
    this.dragLast = { x: e.clientX, y: e.clientY };
  };

  private onPointerUp = (e: PointerEvent): void => {
    if (!this.dragLast) return;
    (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    this.dragLast = null;
    this.panning = false;
  };

  render() {
    const pannable = this.scale > MIN_SCALE;
    return html`
      <div class="toolbar" part="toolbar">
        <sl-tooltip content="Zoom In">
          <sl-icon-button
            name="zoom-in"
            label="Zoom in"
            ?disabled=${this.scale >= MAX_SCALE}
            @click=${() => this.zoomAtCentre(ZOOM_STEP)}
          ></sl-icon-button>
        </sl-tooltip>
        <sl-tooltip content="Zoom Out">
          <sl-icon-button
            name="zoom-out"
            label="Zoom out"
            ?disabled=${this.scale <= MIN_SCALE}
            @click=${() => this.zoomAtCentre(1 / ZOOM_STEP)}
          ></sl-icon-button>
        </sl-tooltip>
        <sl-tooltip content="Fit to screen">
          <sl-icon-button
            name="aspect-ratio"
            label="Fit to screen"
            @click=${() => this.resetView()}
          ></sl-icon-button>
        </sl-tooltip>
        <span class="zoom-readout">${Math.round(this.scale * 100)}%</span>
        <slot name="toolbar-end"></slot>
      </div>
      <div
        class="stage ${pannable ? 'pannable' : ''} ${
          this.panning ? 'panning' : ''
        }"
        @wheel=${this.onWheel}
        @pointerdown=${this.onPointerDown}
        @pointermove=${this.onPointerMove}
        @pointerup=${this.onPointerUp}
        @pointercancel=${this.onPointerUp}
      >
        <div
          class="stage-content"
          style="transform: translate(${this.tx}px, ${this.ty}px) scale(${
            this.scale
          })"
        >
          <ptc-thumbnail
            .grid=${this.grid}
            .assetManager=${this.assetManager}
            fit="contain"
            ?show-dimensions=${this.showDimensions}
          ></ptc-thumbnail>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ptc-zoom-viewer': ShapeZoomViewer;
  }
}
